<template>
    <v-card class="auth-card login-card mx-auto" max-width="1240">
        <div class="auth-card__left">
            <div class="auth-brand">
                <img id="logo-image" alt="Smartdata logo image and dashboard link."
                            rel="noreferrer" class="mx-auto px-5" src="/static/img/logos/Smartdata_dark_L.png">
            </div>

            <div class="auth-form">
                <h1 class="auth-title">Welcome back</h1>
                <p class="auth-subtitle">Sign into your account</p>

                <v-form ref="form" v-model="login.formValid">
                    <v-alert
                        v-if="emailVerificationSuccess"
                        variant="outlined"
                        type="success"
                        class="mb-3"
                    >
                        Your email address has been successfully verified. You may
                        now login.
                    </v-alert>

                    <v-alert
                        v-if="emailVerificationError"
                        variant="outlined"
                        type="error"
                        class="mb-3"
                    >
                        There was an error verifying your email address. Please try
                        again.
                    </v-alert>

                    <v-alert
                        v-if="login.loginInvalidCredentials"
                        variant="outlined"
                        type="error"
                        class="mb-3"
                    >
                        {{ login.invalidCredentialsMsg }}
                    </v-alert>

                    <v-alert
                        v-if="login.genericError"
                        variant="outlined"
                        type="error"
                        class="mb-3"
                    >
                        Unknown error while trying to login.
                    </v-alert>

                    <v-alert
                        v-if="login.unverifiedEmail"
                        variant="outlined"
                        type="warning"
                        class="mb-2"
                    >
                        Email is not verified, you must verify your email before
                        you can login.
                    </v-alert>

                    <v-btn
                        v-if="login.unverifiedEmail"
                        color="primary"
                        variant="text"
                        class="mb-3 px-0"
                        @click="resendEmail"
                    >
                        Resend Email
                    </v-btn>

                    <v-text-field
                        id="email-field"
                        v-model="login.email"
                        autofocus
                        class="auth-input"
                        label="Email address"
                        prepend-inner-icon="$vuetify.icons.email"
                        :rules="[rules.required, rules.email]"
                        variant="outlined"
                        type="email"
                        name="email"
                        autocomplete="email"
                    />

                    <v-text-field
                        id="password-field"
                        v-model="login.password"
                        class="auth-input"
                        prepend-inner-icon="$vuetify.icons.lock"
                        :append-inner-icon="
                            login.showPassword
                                ? '$vuetify.icons.eye'
                                : '$vuetify.icons.eyeSlash'
                        "
                        :type="login.showPassword ? 'text' : 'password'"
                        name="password"
                        label="Password"
                        :rules="[rules.required]"
                        variant="outlined"
                        autocomplete="current-password"
                        @click:append-inner="login.showPassword = !login.showPassword"
                    />

                    <div class="auth-links">
                        <a class="auth-link" href="/accounts/password/reset/request/">
                            Forgot password?
                        </a>
                        <span class="auth-links__sep">|</span>
                        <a class="auth-link" href="/accounts/register/">
                            Create account
                        </a>
                    </div>

                    <v-btn
                        size="large"
                        color="primary"
                        :loading="loader"
                        class="auth-submit"
                        @click="submitLogin"
                    >
                        Log In
                    </v-btn>
                </v-form>
            </div>
        </div>

        <div class="auth-card__right">
            <div class="auth-illustration-wrap">
                <img
                    src="/static/img/smartdata-mockup-transparent.png"
                    alt="Smartdata platform illustration"
                    class="auth-illustration"
                />
            </div>
        </div>

        <generic-snack-bar ref="snackbar" />
        <two-factor-authentication
            ref="twoFactorDialog"
            @token-ready="completeTwoFactorLogin"
        />
    </v-card>
</template>

<script>
import axios from 'axios';
import {
    buildPlanSelectionUrl,
    getRequestedSignupPlanSlug,
    storeSignupPlanFromSearch
} from 'components/BillingPlans/PlanOptions/signupPlanDeepLink';
import GenericSnackBar from 'components/GenericSnackbar/GenericSnackBar.vue';
import TwoFactorAuthentication from 'components/Profile/PhoneNumber/LoginPhoneVerification.vue';

export default {
    name: 'LoginCard',
    components: { TwoFactorAuthentication, GenericSnackBar },
    props: {
        emailVerification: {
            type: [String, Boolean],
            default: ''
        }
    },
    data() {
        return {
            rules: {
                required: value => !!value || 'Required field',
                email: value => {
                    const pattern = /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\d-A-Za-z]+\.)+[A-Za-z]{2,}))$/;
                    return pattern.test(value) || 'Must be a valid e-mail';
                }
            },
            login: {
                email: null,
                password: null,
                showPassword: false,
                formValid: null,
                loginSuccess: false,
                loginInvalidCredentials: false,
                genericError: false,
                unverifiedEmail: false,
                resendEmailLink: null,
                invalidCredentialsMsg: 'Incorrect email or password.'
            },
            emailVerificationSuccess: false,
            emailVerificationError: false,
            loader: false,
            authyId: '',
            onEnterKeyup: null
        };
    },
    mounted() {
        storeSignupPlanFromSearch(window.location.search);
        this.onEnterKeyup = event => {
            if (event.key === 'Enter') {
                this.submitLogin();
            }
        };
        window.addEventListener('keyup', this.onEnterKeyup);

        if (String(this.emailVerification) === 'True') {
            this.emailVerificationSuccess = true;
        } else if (String(this.emailVerification) === 'False') {
            this.emailVerificationError = true;
        }
    },
    beforeUnmount() {
        if (this.onEnterKeyup) {
            window.removeEventListener('keyup', this.onEnterKeyup);
        }
    },
    methods: {
        async validateForm() {
            const validationResult = await this.$refs.form.validate();
            if (typeof validationResult === 'boolean') {
                return validationResult;
            }
            if (
                validationResult &&
                typeof validationResult === 'object' &&
                'valid' in validationResult
            ) {
                return Boolean(validationResult.valid);
            }
            return false;
        },
        completeTwoFactorLogin(authyToken) {
            this.$refs.twoFactorDialog.close();
            this.loader = true;

            axios
                .post('/accounts/login/', {
                    email: this.login.email,
                    password: this.login.password,
                    token: authyToken,
                    requestedPlanSlug: getRequestedSignupPlanSlug()
                })
                .then(response => {
                    if (response.data.twoFactorFailed) {
                        this.$refs.twoFactorDialog.init(authyToken, true);
                    } else if (response.data.success) {
                        this.loginSuccess();
                    }
                })
                .catch(error => {
                    window.dialog(
                        'Sorry but there was an error logging in. Please try again later or contact support.'
                    );
                    throw error;
                });
        },
        loginSuccess() {
            this.login.loginSuccess = true;
            const urlParameters = new URLSearchParams(window.location.search);
            const redirectTo = urlParameters.get('next');
            if (redirectTo) {
                document.location = redirectTo;
                return;
            }

            const requestedPlanSlug = getRequestedSignupPlanSlug();
            document.location = requestedPlanSlug
                ? buildPlanSelectionUrl(requestedPlanSlug)
                : '/';
        },
        async submitLogin() {
            const isValid = await this.validateForm();
            if (!isValid) return;

            this.loader = true;
            this.turnOffMessages();
            axios
                .post('/accounts/login/', {
                    email: this.login.email,
                    password: this.login.password,
                    requestedPlanSlug: getRequestedSignupPlanSlug()
                })
                .then(response => {
                    this.loader = false;
                    if (response.data.success === true) {
                        this.loginSuccess();
                    } else if (response.data.unverifiedEmail) {
                        this.login.unverifiedEmail = true;
                        this.login.resendEmailLink = response.data.resendEmailLink;
                    } else if (response.data.loginInvalidCredentials) {
                        this.login.loginInvalidCredentials = true;
                        this.login.invalidCredentialsMsg = response.data.message;
                    } else if (response.data.twoFactorRequired) {
                        this.authyId = response.data.authyId;
                        this.$refs.twoFactorDialog.init(this.authyId);
                    }
                })
                .catch(error => {
                    this.loader = false;
                    this.login.genericError = true;
                    throw error;
                });
        },
        resendEmail() {
            axios
                .get(this.login.resendEmailLink)
                .then(response => response.data)
                .then(data => {
                    if (data.success) {
                        this.$refs.snackbar.show(
                            'The verification email has been resent.'
                        );
                    } else {
                        this.$refs.snackbar.show(
                            'There was an error resending the email.'
                        );
                    }
                })
                .catch(error => {
                    this.$refs.snackbar.show(
                        'There was an error resending the email.'
                    );
                    throw error;
                });
        },
        turnOffMessages() {
            this.login.loginInvalidCredentials = false;
            this.emailVerificationError = false;
            this.emailVerificationSuccess = false;
            this.login.loginSuccess = false;
            this.login.unverifiedEmail = false;
            this.login.genericError = false;
        }
    }
};
</script>

<style scoped>
.auth-card {
    display: grid;
    grid-template-columns: 1fr 1.15fr;
    border-radius: 22px !important;
    overflow: hidden;
    box-shadow: 0 24px 48px rgba(20, 29, 71, 0.14) !important;
}

.auth-card__left {
    background: #f6f8fc;
    padding: 42px 52px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.auth-brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: #1ba0e6;
    margin-bottom: 20px;
}

.auth-brand__mark {
    width: 26px;
    height: 26px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 700;
    background: #1ba0e6;
}

.auth-brand__name {
    font-size: 1.55rem;
    font-weight: 600;
    color: #1092d5;
}

.auth-form {
    max-width: 430px;
    width: 100%;
}

.auth-title {
    font-size: clamp(2rem, 2.8vw, 3rem);
    line-height: 1.08;
    margin-bottom: 6px;
    color: #1e2564;
}

.auth-subtitle {
    color: #55608f;
    font-size: 1.15rem;
    margin-bottom: 24px;
}

.auth-input {
    margin-bottom: 10px;
}

.auth-input :deep(.v-field) {
    background: #ffffff;
    border-radius: 12px;
}

.auth-links {
    margin: 8px 0 18px;
    color: #4f5f88;
    font-size: 0.97rem;
}

.auth-links__sep {
    margin: 0 10px;
    color: #9aa4c5;
}

.auth-link {
    color: #1f9ae2;
    text-decoration: none;
}

.auth-link:hover {
    text-decoration: underline;
}

.auth-submit {
    min-width: 180px;
    height: 50px;
    border-radius: 14px !important;
    letter-spacing: 0.02em;
    text-transform: none;
    font-size: 1.12rem !important;
}

.auth-card__right {
    position: relative;
    background: linear-gradient(160deg, #4e4fe7 0%, #38a9e6 55%, #37b6d8 100%);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 30px 20px 0;
}

.auth-card__right::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
        radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.25), transparent 42%),
        linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0));
}

.auth-illustration-wrap {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: center;
}

.auth-illustration {
    width: min(100%, 760px);
    max-height: 100%;
    object-fit: contain;
    transform: translateX(-4%);
    filter: drop-shadow(0 14px 20px rgba(8, 24, 72, 0.3));
}

@media (max-width: 1100px) {
    .auth-card {
        grid-template-columns: 1fr;
    }

    .auth-card__right {
        min-height: 280px;
        padding-top: 12px;
    }

    .auth-illustration {
        transform: none;
        width: min(92%, 560px);
    }
}

@media (max-width: 700px) {
    .auth-card__left {
        padding: 28px 22px;
    }

    .auth-brand {
        margin-bottom: 8px;
    }

    .auth-card__right {
        min-height: 220px;
    }
}
</style>
